"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function ScribbleUnderline() {
  return <svg className="absolute -bottom-2 left-0 w-full" height="10" viewBox="0 0 200 10" preserveAspectRatio="none" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M2 6.5 Q 25 2, 50 5 T 100 5.5 T 150 4 T 198 6" stroke="var(--primary)" strokeWidth="2.5" strokeLinecap="round" fill="none" />
    </svg>;
}
function ProductsGridConsumer() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  const tints = ["bg-accent/50", "bg-accent"];
  if (loading) {
    return <div className="grid grid-cols-2 gap-6 md:grid-cols-3 md:gap-8 lg:grid-cols-4">
        {[...Array(8)].map((_, i) => <div key={i} data-index={i} className="rounded-lg border border-border bg-card p-5">
            <div className="aspect-square rounded-lg bg-muted animate-pulse" />
            <div className="mt-4 h-5 w-3/4 rounded bg-muted animate-pulse" />
            <div className="mt-2 h-4 w-1/2 rounded bg-muted animate-pulse" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-sm text-muted-foreground space-y-3">
        <p>Failed to load products.</p>
        <Button onClick={refetch} variant="outline" size="sm">
          Retry
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <p className="text-muted-foreground">No hygiene essentials available right now.</p>;
  }
  return <div className="grid grid-cols-2 gap-6 md:grid-cols-3 md:gap-8 lg:grid-cols-4">
      {products.slice(0, 8).map((product, i) => {
      const tint = tints[i % tints.length];
      const isNew = product.tags?.some(t => t.slug === "new-arrivals" || t.name?.toLowerCase().includes("new"));
      const displayPrice = product.discounted_price && product.discounted_price > 0 ? product.discounted_price : product.price;
      return <article data-index={i} key={product.id} className="group flex flex-col rounded-lg border border-border bg-card p-4 md:p-5 transition-all duration-240 hover:-translate-y-1 shadow-md">
            <Link to={product.link} className="block">
              <div className={`relative aspect-square overflow-hidden rounded-lg ${tint}`}>
                {product.featured_image_url && <img src={product.featured_image_url} alt={product.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-240 group-hover:scale-105" />}

                {displayPrice !== undefined && <span className="absolute bottom-3 left-3 inline-flex items-center rounded-lg bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">
                    {`$${Number(displayPrice).toFixed(2)}`}
                  </span>}

                {isNew && <span className="absolute bottom-3 right-3 inline-flex items-center rounded-lg border border-foreground bg-card px-3 py-1 text-xs font-semibold text-foreground">
                    New
                  </span>}
              </div>
            </Link>

            <div className="mt-4 flex flex-1 flex-col">
              <Link to={product.link}>
                <h3 className="font-default text-base md:text-lg font-semibold text-foreground leading-snug">
                  {product.title}
                </h3>
              </Link>
              <p className="mt-1 text-sm text-muted-foreground">
                {"Portable hygiene mist"}
              </p>

              <div className="mt-4">
                <AddToCartButton productId={product.id} variant="default" size="sm" className="w-full" buttonClassName="bg-primary text-primary-foreground rounded-lg w-full" text="Add to cart" showQuantity={false} />
              </div>
            </div>
          </article>;
    })}
    </div>;
}
export default function Essentials() {
  const wp_query_params = {
    per_page: 8,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="6193" id="travelers-essentials" className="dark bg-background py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div className="sm:max-w-2xl">
            <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight text-foreground leading-[1.04]">
              {"Traveler's "}
              <span className="relative inline-block italic font-normal">
                {"essentials"}
                <ScribbleUnderline />
              </span>
            </h2>
          </div>

          <div className="shrink-0">
            <Button asChild variant="link" className="group h-auto p-0 text-foreground hover:no-underline">
              <Link to="/ShopPage" className="inline-flex items-center gap-2 text-base font-semibold">
                <span className="relative">
                  {"View all products"}
                  <span className="absolute left-0 -bottom-0.5 h-[2px] w-0 bg-primary transition-all duration-260 ease-out group-hover:w-full" />
                </span>
                <ArrowRight className="h-4 w-4 transition-transform duration-260 group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>
        </div>

        <div className="mt-12 md:mt-16">
          <EcommerceProductsProvider wp_query={{
          per_page: 8,
          order: "desc",
          orderby: "date"
        }}>
            <ProductsGridConsumer />
          </EcommerceProductsProvider>
        </div>
      </div>
    </section>;
}